/** Set by PrintView when browserless may capture the report PDF. */
export const REPORT_PDF_READY_ATTR = 'data-report-pdf-ready';

/**
 * Set on `<html>` when the report cover image fetch has settled (`ready`,
 * `missing`, or `error`) and a stored image — if any — has been decoded.
 */
export const REPORT_COVER_SETTLED_ATTR = 'data-report-cover-settled';

type ReportPdfCaptureReadyInput = {
  pageCount: number;
  minPageCount: number;
  coverSettled: boolean;
};

/**
 * Whether PrintView may start the PDF-ready debounce.
 * Pages must be mounted and the cover image request must have settled.
 */
export const isReportPdfCaptureReady = (
  input: ReportPdfCaptureReadyInput,
): boolean => input.pageCount >= input.minPageCount && input.coverSettled;

export type PdfReadyDebounceAction = 'cancel' | 'reschedule';

/**
 * Whether PrintView should drop or restart the PDF-ready debounce.
 * {@link PdfReadyDebounceAction} `cancel` when pages or cover are not ready so
 * an in-flight timer cannot mark the PDF ready on an empty cover slot.
 */
export const getPdfReadyDebounceAction = (
  input: ReportPdfCaptureReadyInput,
): PdfReadyDebounceAction =>
  isReportPdfCaptureReady(input) ? 'reschedule' : 'cancel';

/**
 * Read whether the cover image is settled for PDF capture.
 */
export const isReportCoverSettled = (): boolean =>
  document.documentElement.getAttribute(REPORT_COVER_SETTLED_ATTR) === 'true';

export type ReportPdfFallbackAction = 'signal' | 'observe-cover';

/**
 * After the page-mount timeout, only mark the PDF ready if the cover has
 * settled. Otherwise keep watching {@link REPORT_COVER_SETTLED_ATTR}.
 */
export const getReportPdfFallbackAction = (
  coverSettled: boolean,
): ReportPdfFallbackAction => (coverSettled ? 'signal' : 'observe-cover');

/**
 * After attaching a MutationObserver for {@link REPORT_COVER_SETTLED_ATTR},
 * re-read settle state. The observer does not fire for an attribute already set.
 */
export const shouldSignalReadyAfterCoverObserve = (
  coverSettled: boolean,
): boolean => coverSettled;

/**
 * Publish cover-image settled state for {@link PrintView} / browserless.
 */
export const setReportCoverSettled = (settled: boolean): void => {
  if (settled) {
    document.documentElement.setAttribute(REPORT_COVER_SETTLED_ATTR, 'true');
    return;
  }
  document.documentElement.removeAttribute(REPORT_COVER_SETTLED_ATTR);
};
